/**
 * Session Timeout Utility
 * Clients: 10 minutes of inactivity. Admin / Manager / Employee: 30 minutes.
 */

import {
  SESSION_LAST_ACTIVITY_KEY,
  getInactivityTimeoutMs,
  getSessionLastActivity,
  isSessionExpired,
  touchSessionActivity,
} from "./sessionAuth";

class SessionTimeout {
  constructor(logoutCallback) {
    this.logoutCallback = logoutCallback;
    this.timeoutId = null;
    this.lastActivityTime = null;
    this.events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];
    this.resetTimeout = this.resetTimeout.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleWindowFocus = this.handleWindowFocus.bind(this);
    this.handleWindowBlur = this.handleWindowBlur.bind(this);
    this.handleStorage = this.handleStorage.bind(this);
  }

  /**
   * Get last activity time across all tabs (from localStorage or this tab)
   */
  getLastActivityTime() {
    const fromStorage = getSessionLastActivity();
    const local = this.lastActivityTime || 0;
    return Math.max(fromStorage, local);
  }

  /**
   * Get inactivity timeout in ms: 10 min for client, 30 min for admin/manager/employee
   */
  getInactivityTimeout() {
    return getInactivityTimeoutMs();
  }

  scheduleTimeoutFromLastActivity() {
    const inactivityTimeout = this.getInactivityTimeout();
    const lastActivity = this.getLastActivityTime();
    this.clearTimeout();

    if (!lastActivity) {
      this.resetTimeout();
      return;
    }

    const timeSinceLastActivity = Date.now() - lastActivity;
    if (timeSinceLastActivity >= inactivityTimeout) {
      if (this.logoutCallback) this.logoutCallback();
      return;
    }

    const remainingTime = inactivityTimeout - timeSinceLastActivity;
    this.timeoutId = window.setTimeout(
      () => this.onInactivityExpired(),
      remainingTime,
    );
  }

  /**
   * Initialize the session timeout
   * Starts listening to user activity events
   */
  init() {
    if (!localStorage.getItem("user_id")) {
      return;
    }

    if (isSessionExpired()) {
      if (this.logoutCallback) this.logoutCallback();
      return;
    }

    this.scheduleTimeoutFromLastActivity();

    this.events.forEach((event) => {
      document.addEventListener(event, this.resetTimeout, true);
    });

    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("focus", this.handleWindowFocus);
    window.addEventListener("blur", this.handleWindowBlur);
    window.addEventListener("storage", this.handleStorage);
  }

  /**
   * When another tab reports activity (updates session_last_activity), reset this tab's timeout
   */
  handleStorage(e) {
    if (e.key === SESSION_LAST_ACTIVITY_KEY && e.newValue) {
      const ts = parseInt(e.newValue, 10);
      if (ts && localStorage.getItem("user_id")) {
        this.lastActivityTime = ts;
        this.scheduleTimeoutFromLastActivity();
      }
    }
  }

  /**
   * Handle window focus (when browser window regains focus)
   * Checks if session expired while system was off/asleep
   */
  handleWindowFocus() {
    if (!document.hidden && localStorage.getItem("user_id")) {
      if (isSessionExpired()) {
        if (this.logoutCallback) this.logoutCallback();
        return;
      }
      this.scheduleTimeoutFromLastActivity();
    }
  }

  /**
   * Handle window blur (when browser window loses focus)
   */
  handleWindowBlur() {
    // Keep tracking, but don't clear timeout
  }

  /**
   * Handle visibility change (tab/window focus)
   * Also handles system sleep/wake scenarios
   */
  handleVisibilityChange() {
    if (document.hidden) {
      this.clearTimeout();
    } else if (localStorage.getItem("user_id")) {
      if (isSessionExpired()) {
        if (this.logoutCallback) this.logoutCallback();
        return;
      }
      this.scheduleTimeoutFromLastActivity();
    }
  }

  /**
   * Called when this tab's inactivity timer fires. Only logout if no tab had recent activity.
   */
  onInactivityExpired() {
    if (!localStorage.getItem("user_id")) return;
    if (isSessionExpired()) {
      if (this.logoutCallback) this.logoutCallback();
      return;
    }
    this.scheduleTimeoutFromLastActivity();
  }

  /**
   * Reset the inactivity timeout on real user activity.
   */
  resetTimeout() {
    this.lastActivityTime = Date.now();
    touchSessionActivity(this.lastActivityTime);
    this.clearTimeout();

    if (!localStorage.getItem("user_id")) {
      this.destroy();
      return;
    }

    const inactivityTimeout = this.getInactivityTimeout();
    this.timeoutId = window.setTimeout(
      () => this.onInactivityExpired(),
      inactivityTimeout,
    );
  }

  clearTimeout() {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  destroy() {
    this.clearTimeout();
    this.lastActivityTime = null;

    this.events.forEach((event) => {
      document.removeEventListener(event, this.resetTimeout, true);
    });
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("focus", this.handleWindowFocus);
    window.removeEventListener("blur", this.handleWindowBlur);
    window.removeEventListener("storage", this.handleStorage);
  }
}

export default SessionTimeout;
